import {
  AWPAudioTrack,
  AWPTextTrack,
  AWPTextTrackKind,
  Locale,
  localPreferences,
  NO_TEXT_TRACK,
  Track,
} from "@tv4/avod-web-player-common";
import { isSupportedOS as isDesktopOS } from "@tv4/avod-web-player-device-capabilities";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import Check from "../../../icons/check.svg";
import CloseIconSvg from "../../../icons/CloseCircle.svg";
import { useControls, usePlayerState } from "../../../providers/CoreProvider";
import {
  useLayoutOrientation,
  useLayoutSettings,
  useLayoutSize,
  useTracksMenuOpen,
} from "../../../providers/LayoutProvider";
import {
  useLocale,
  useTranslations,
} from "../../../providers/TranslationsProvider";
import { LayoutOrientation, LayoutSize } from "../../../util/device";
import { getLanguageDisplayName } from "../../../util/languageDisplayName";
import TextTrackControls from "../TextTrackControls";
import { TracksButton } from "./TracksButton";
import {
  CloseIcon,
  Divider,
  NoTextTracksAvailable,
  TrackItem,
  TrackListContainer,
  TracksContainer,
  TracksMenuContainer,
  TracksWrapper,
  TrackTitle,
} from "./TracksMenu.styles";
import { usePreferredTextSize } from "./usePreferredTextSize";

const getTrackTranslation = (
  locale: Locale,
  translations: ReturnType<typeof useTranslations>,
  track: AWPTextTrack | AWPAudioTrack
) => {
  let translation: string;
  if (track.language) {
    translation = getLanguageDisplayName(locale, track.language);
  } else if ("kind" in track && track.kind === AWPTextTrackKind.NOTHING) {
    translation = translations("language__subtitles_off");
  } else {
    translation = track.label;
  }
  return translation || "";
};

type TracksListProps = {
  tracks: (AWPTextTrack | AWPAudioTrack)[];
  title: string;
  activeTrack: Track | null | undefined;
  autoFocus?: boolean;
  onClick: (track: Track) => void;
  $stackVertically: boolean;
};

const TracksList = ({
  tracks,
  title,
  activeTrack,
  onClick,
  $stackVertically,
  autoFocus = false,
}: TracksListProps) => {
  const locale = useLocale();
  const translations = useTranslations();
  return (
    <TrackListContainer role="radiogroup">
      <TrackTitle $stackVertically={$stackVertically}>{title}</TrackTitle>
      {tracks.map((track, i) => {
        const isActive = activeTrack?.id === track.id;
        const trackLabel = getTrackTranslation(locale, translations, track);
        let voiceOverLabel = trackLabel;
        if ("kind" in track && track.kind === AWPTextTrackKind.NOTHING) {
          voiceOverLabel = `${title} ${translations("language__subtitles_off")}`;
        }

        return (
          <TrackItem
            role="radio"
            aria-label={voiceOverLabel}
            onClick={() => onClick(track)}
            className={isActive ? "active" : ""}
            aria-checked={isActive}
            key={track.id + i}
            autoFocus={autoFocus && isActive}
          >
            <div style={{ marginRight: "10px", width: "16px" }}>
              {isActive && <Check width="16" />}
            </div>
            {trackLabel}
          </TrackItem>
        );
      })}
    </TrackListContainer>
  );
};

function Tracks({
  closeTracksMenu,
  displayCover,
}: {
  closeTracksMenu: () => void;
  displayCover: boolean;
}) {
  const {
    activeTextTrack,
    activeAudioTrack,
    audioTracks,
    textTracks,
    isLive,
    isFullscreen,
  } = usePlayerState();
  const controls = useControls();
  const layoutSize = useLayoutSize();
  const layoutOrientation = useLayoutOrientation();

  const translations = useTranslations();

  const handleTextTrackClick = useCallback(
    (textTrack: Track) => {
      controls?.setTextTrack?.(textTrack as AWPTextTrack);
      localPreferences.setPreferredText(textTrack as AWPTextTrack, isLive);
      closeTracksMenu();
    },
    [controls, isLive, closeTracksMenu]
  );

  const handleAudioTrackClick = useCallback(
    (audioTrack: Track) => {
      controls?.setAudioTrack?.(audioTrack as AWPAudioTrack);
      localPreferences.setPreferredAudio(audioTrack.language);
      closeTracksMenu();
    },
    [controls, closeTracksMenu]
  );

  const showTextTracks = textTracks.length > 1;
  const showAudioTracks = audioTracks.length > 1;

  const stackOptionsVertically =
    layoutSize === LayoutSize.SMALLEST ||
    (isFullscreen && layoutOrientation === LayoutOrientation.PORTRAIT) ||
    (!isFullscreen &&
      layoutOrientation === LayoutOrientation.PORTRAIT &&
      !isDesktopOS());

  return (
    <TracksContainer $displayCover={displayCover}>
      <TracksMenuContainer $displayCover={displayCover}>
        <TracksWrapper
          $displayCover={displayCover}
          $stackVertically={stackOptionsVertically}
        >
          {!showTextTracks && !showAudioTracks ? (
            <NoTextTracksAvailable
              aria-label={translations("language__no_subtitles")}
            >
              {translations("language__no_subtitles")}
            </NoTextTracksAvailable>
          ) : (
            <>
              {showTextTracks && (
                <TracksList
                  tracks={textTracks}
                  activeTrack={activeTextTrack}
                  autoFocus={true}
                  title={translations("language__subtitles")}
                  onClick={handleTextTrackClick}
                  $stackVertically={stackOptionsVertically}
                />
              )}
              {showAudioTracks && (
                <>
                  {showTextTracks && (
                    <Divider $stackVertically={stackOptionsVertically} />
                  )}
                  <TracksList
                    tracks={audioTracks}
                    activeTrack={activeAudioTrack}
                    autoFocus={!showTextTracks}
                    title={translations("language__audio")}
                    onClick={handleAudioTrackClick}
                    $stackVertically={stackOptionsVertically}
                  />
                </>
              )}
              {showTextTracks && (
                <>
                  <Divider $stackVertically={stackOptionsVertically} />
                  <TextTrackControls
                    onSelected={() => {
                      closeTracksMenu();
                    }}
                    $stackVertically={stackOptionsVertically}
                  />
                </>
              )}
            </>
          )}
        </TracksWrapper>
      </TracksMenuContainer>
      {displayCover && (
        <CloseIcon onClick={closeTracksMenu}>
          <CloseIconSvg />
        </CloseIcon>
      )}
    </TracksContainer>
  );
}

type TTracksHandler = {
  TracksButton?: FunctionComponent<Partial<Parameters<typeof TracksButton>[0]>>;
  TracksMenu?: FunctionComponent<Partial<Parameters<typeof Tracks>[0]>>;
  closeTracksMenu: () => void;
  tracksMenuOpen: boolean;
};

export const useTracksMenu = (): TTracksHandler => {
  const { isAd, isPauseAd } = usePlayerState();
  const locale = useLocale();

  const showTracks =
    !useLayoutSettings().hideSubtitlesMenu && !isPauseAd && !isAd;

  const { activeTextTrack, activeAudioTrack, audioTracks, textTracks } =
    usePlayerState();
  const layoutSize = useLayoutSize();
  const { tracksMenuOpen, setTracksMenuOpen } = useTracksMenuOpen();

  const hasTracksToChooseFrom = audioTracks.length > 1 || textTracks.length > 1;

  const translations = useTranslations();

  usePreferredTextSize();

  const activeTextTrackLabel = activeTextTrack?.label || "";
  const activeTextTrackLanguage = activeTextTrack?.language || "";

  const activeAudioTrackLabel = activeAudioTrack?.label || "";
  const activeAudioTrackLanguage = activeAudioTrack?.language || "";

  const textTracksCount = textTracks?.length || 0;
  const audioTracksCount = audioTracks?.length || 0;

  const isSmallLayout = [LayoutSize.SMALL, LayoutSize.SMALLEST].includes(
    layoutSize
  );

  const audioAndTextLabel = useMemo(() => {
    if (!hasTracksToChooseFrom) return translations("language__no_subtitles");

    const getTextTrackLabel = (): string => {
      if (activeTextTrackLanguage) {
        return getLanguageDisplayName(locale, activeTextTrackLanguage);
      }
      if (textTracksCount > 1) {
        // if there are more than 1 subtitle, but no activeTextTrack then display as no subtitles
        return getTrackTranslation(locale, translations, NO_TEXT_TRACK);
      }
      return activeTextTrackLabel || "";
    };

    const getAudioTrackLabel = () => {
      return activeAudioTrackLanguage &&
        activeAudioTrackLanguage !== "0" &&
        activeAudioTrackLanguage !== "und"
        ? getLanguageDisplayName(locale, activeAudioTrackLanguage)
        : activeAudioTrackLabel;
    };

    const textTrackLabel = getTextTrackLabel();
    const audioTrackLabel = getAudioTrackLabel();
    const audioTranslation = translations("language__audio");

    if (!audioTrackLabel || audioTracksCount < 2) return textTrackLabel;
    if (!textTrackLabel || textTracksCount < 2)
      return `${audioTranslation}: ${audioTrackLabel}`;

    const separator = textTrackLabel !== "" ? ", " : "";

    return `${textTrackLabel}${separator}${audioTranslation}: ${audioTrackLabel}`;
  }, [
    activeTextTrackLabel,
    activeTextTrackLanguage,
    activeAudioTrackLabel,
    activeAudioTrackLanguage,
    audioTracksCount,
    hasTracksToChooseFrom,
    locale,
    textTracksCount,
    translations,
  ]);

  const displayCover = isSmallLayout || !isDesktopOS();

  // set as closed when unmounted
  useEffect(() => () => setTracksMenuOpen(false), [setTracksMenuOpen]);

  const handleTracksButtonClick = useCallback(() => {
    setTracksMenuOpen(!tracksMenuOpen);
  }, [setTracksMenuOpen, tracksMenuOpen]);

  const buttonRef = useRef<null | HTMLButtonElement>(null);

  const closeTracksMenu = useCallback(() => {
    setTimeout(() => buttonRef.current?.focus(), 0);

    if (tracksMenuOpen) {
      setTracksMenuOpen(false);
    }
  }, [setTracksMenuOpen, tracksMenuOpen]);

  return {
    tracksMenuOpen,
    closeTracksMenu,
    TracksButton: useMemo(
      () => (props?: Partial<Parameters<typeof TracksButton>[0]>) =>
        showTracks ? (
          <TracksButton
            buttonRef={buttonRef}
            onClick={handleTracksButtonClick}
            hasTracksToChooseFrom={hasTracksToChooseFrom}
            label={audioAndTextLabel}
            aria-label={audioAndTextLabel}
            {...props}
          />
        ) : null,
      [
        handleTracksButtonClick,
        hasTracksToChooseFrom,
        audioAndTextLabel,
        showTracks,
      ]
    ),
    TracksMenu: useMemo(
      () => (props?: Partial<Parameters<typeof Tracks>[0]>) =>
        showTracks && tracksMenuOpen ? (
          <Tracks
            closeTracksMenu={closeTracksMenu}
            displayCover={displayCover}
            {...props}
          />
        ) : null,
      [closeTracksMenu, showTracks, tracksMenuOpen, displayCover]
    ),
  };
};
